// ─────────────────────────────────────────────────────────────────────────────
// voucherService — the ADMIN side of the Pro-giveaway voucher system.
//
// Only the allowlisted admin account can touch these collections; firestore.rules
// denies every other client even read access, because a readable voucherCodes
// would let any consumer dump the collection and redeem every code in it.
//
// Redemption is NOT here. It lives entirely in the redeemVoucher callable, which
// validates, claims and grants inside one transaction using the Admin SDK. This
// module never writes users/{uid}.proGrant* or voucherRedemptions — the rules
// pin both, and the redemptionCount ledger is the function's to increment.
//
// Data model (mirrors functions/redeemVoucher.js):
//   voucherCodes/{CODE}          CODE is the NORMALIZED form — uppercase, no
//                                spaces or dashes — because the callable looks
//                                the doc up by that exact id.
//     { months, maxRedemptions, redemptionCount, active, expiresAt?, campaign?,
//       createdAt }
//   voucherRedemptions/{CODE}_{uid}   existence = "already redeemed"
// ─────────────────────────────────────────────────────────────────────────────

import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
} from 'firebase/firestore';
import { db } from './config';

// Matches MAX_CODE_LENGTH in functions/redeemVoucher.js — a longer code could
// be created here but never redeemed, since the callable rejects it outright.
export const MAX_CODE_LENGTH = 32;

// Ambiguous glyphs are left out so a code read off a flyer or a story slide
// can't be mistyped as a different valid code: no O/0, I/1/L, U/V, S/5, B/8.
const CODE_ALPHABET = 'ACDEFGHJKMNPQRTWXY2346789';

const GENERATED_LENGTH = 8;

/**
 * Normalizes a code the SAME way functions/redeemVoucher.js does. Both sides
 * must agree or a code created here will not be found at redemption time.
 *
 * @param {string} raw Code as typed.
 * @return {string} Uppercase, with spaces and dashes stripped.
 */
export function normalizeCode(raw) {
  return String(raw ?? '')
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '');
}

/**
 * Builds a random code from the unambiguous alphabet. Uses crypto so two codes
 * generated in the same millisecond can't collide the way Math.random seeded
 * per-tick can.
 *
 * @param {string} [prefix] Optional campaign prefix, e.g. 'LAUNCH'.
 * @return {string} Normalized code ready to create.
 */
export function generateCode(prefix = '') {
  const bytes = new Uint32Array(GENERATED_LENGTH);
  globalThis.crypto.getRandomValues(bytes);
  const body = Array.from(
    bytes,
    (n) => CODE_ALPHABET[n % CODE_ALPHABET.length]
  ).join('');
  return normalizeCode(`${prefix}${body}`).slice(0, MAX_CODE_LENGTH);
}

/**
 * Live subscription to every voucher code, newest first. The admin console
 * renders from this rather than a one-shot read so redemptionCount ticks up on
 * screen as the callable increments it.
 *
 * @param {Function} onNext Receives the array of codes.
 * @param {Function} onError Receives a listener failure.
 * @return {Function} Unsubscribe.
 */
export function subscribeToVoucherCodes(onNext, onError) {
  return onSnapshot(
    collection(db, 'voucherCodes'),
    (snap) => {
      const codes = snap.docs.map((d) => ({ code: d.id, ...d.data() }));
      // Sorted client-side: createdAt is absent on any code seeded by hand in
      // the console, and orderBy would drop those documents entirely.
      codes.sort(
        (a, b) =>
          (b.createdAt?.toMillis?.() ?? 0) - (a.createdAt?.toMillis?.() ?? 0)
      );
      onNext(codes);
    },
    onError
  );
}

/**
 * Creates one voucher code.
 *
 * @param {Object} params Voucher settings.
 * @param {string} params.code Code as typed; normalized before use.
 * @param {number} params.months Months of Pro granted per redemption.
 * @param {number} params.maxRedemptions How many users may redeem it.
 * @param {string} [params.campaign] Free-text label for reporting.
 * @param {?Date} [params.expiresAt] Campaign cutoff — the LAST moment the code
 *   can be redeemed. Distinct from the granted Pro period.
 * @return {Promise<string>} The normalized code that was created.
 */
export async function createVoucherCode({
  code,
  months,
  maxRedemptions,
  campaign = '',
  expiresAt = null,
}) {
  const normalized = normalizeCode(code);

  if (!normalized) throw new Error('Enter a code.');
  if (normalized.length > MAX_CODE_LENGTH) {
    throw new Error(`Codes can be at most ${MAX_CODE_LENGTH} characters.`);
  }

  const monthsNum = Number(months);
  if (!Number.isInteger(monthsNum) || monthsNum < 1) {
    throw new Error('Months must be a whole number of 1 or more.');
  }

  const maxNum = Number(maxRedemptions);
  if (!Number.isInteger(maxNum) || maxNum < 1) {
    throw new Error('Max redemptions must be a whole number of 1 or more.');
  }

  if (expiresAt && expiresAt.getTime() <= Date.now()) {
    throw new Error('Campaign expiry must be in the future.');
  }

  // Guard against silently overwriting a live code and resetting its
  // redemptionCount, which would hand out fresh uses of a spent code. The web
  // SDK has no create-only write, so this is a read-then-write; a single admin
  // account makes the race academic.
  const ref = doc(db, 'voucherCodes', normalized);
  const existing = await getDoc(ref);
  if (existing.exists()) {
    throw new Error(`${normalized} already exists.`);
  }

  await setDoc(ref, {
    months: monthsNum,
    maxRedemptions: maxNum,
    redemptionCount: 0,
    active: true,
    campaign: campaign.trim(),
    ...(expiresAt ? { expiresAt: Timestamp.fromDate(expiresAt) } : {}),
    createdAt: serverTimestamp(),
  });

  return normalized;
}

/**
 * Pauses or resumes a code. Pausing is the reversible way to retire one — the
 * callable treats active:false as "invalid" while the redemption history and
 * the count survive.
 *
 * @param {string} code Normalized code / document id.
 * @param {boolean} active Whether the code should be redeemable.
 * @return {Promise<void>}
 */
export async function setVoucherActive(code, active) {
  await updateDoc(doc(db, 'voucherCodes', code), { active: Boolean(active) });
}

/**
 * Hard-deletes a code. Callers must restrict this to codes with no redemptions
 * (the console does) — deleting a redeemed code destroys the only record of
 * what the campaign gave away, and the grants it already made live on.
 *
 * @param {string} code Normalized code / document id.
 * @return {Promise<void>}
 */
export async function deleteVoucherCode(code) {
  await deleteDoc(doc(db, 'voucherCodes', code));
}

/**
 * Derives the single status a code should display. Order matters: a paused
 * code reads "Paused" even if it is also exhausted, because un-pausing is the
 * admin's next action either way.
 *
 * @param {Object} voucher A voucher code document.
 * @return {{label: string, tone: string}} Label and Brand chip tone.
 */
export function voucherStatus(voucher) {
  const used = voucher.redemptionCount ?? 0;
  const max = voucher.maxRedemptions ?? 1;

  if (voucher.active === false) return { label: 'Paused', tone: 'neutral' };
  if (voucher.expiresAt && voucher.expiresAt.toMillis() < Date.now()) {
    return { label: 'Expired', tone: 'danger' };
  }
  if (used >= max) return { label: 'Exhausted', tone: 'warn' };
  return { label: 'Active', tone: 'success' };
}
